import { useRef, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Reveal, SectionLabel } from "../Reveal";
import { MAP_LOCATIONS, MAP_LEGEND } from "../../data/content";

const COLORS = {
  spartan: "#BAF173",
  hyrox: "#6735FA",
  volunteer: "#11E5B4",
  lived: "#F4A64B",
};

const makeIcon = (type) => {
  const color = COLORS[type] || "#BAF173";
  return L.divIcon({
    className: "",
    html: `<div class="rc-marker"><span class="pulse" style="box-shadow:0 0 0 0 ${color}99"></span><span class="dot" style="background:${color};box-shadow:0 0 12px ${color}"></span></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });
};

// Listens for "reycro:flyto" events (fired from the Race History cards) and
// flies the map to the matching pin + opens its popup.
const FlyController = ({ markerRefs }) => {
  const map = useMap();
  useEffect(() => {
    const handler = (e) => {
      const loc = MAP_LOCATIONS.find((l) => l.id === e.detail);
      if (!loc) return;
      map.flyTo([loc.lat, loc.lng], 6, { duration: 1.2 });
      setTimeout(() => markerRefs.current[loc.id]?.openPopup(), 1300);
    };
    window.addEventListener("reycro:flyto", handler);
    return () => window.removeEventListener("reycro:flyto", handler);
  }, [map, markerRefs]);
  return null;
};

// Gentle one-time zoom toward the European race cluster when the map scrolls into view.
const IntroFly = () => {
  const map = useMap();
  useEffect(() => {
    const el = document.getElementById("map");
    if (!el) return;
    let done = false;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !done) {
            done = true;
            map.flyTo([43, 1], 4, { duration: 2.6, easeLinearity: 0.15 });
          }
        });
      },
      { threshold: 0.45 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [map]);
  return null;
};

export const WorldMap = () => {
  const markerRefs = useRef({});

  return (
    <section id="map" className="relative py-24 sm:py-32 md:py-40 bg-[#050505] bg-noise border-t border-white/10" data-testid="map-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <SectionLabel color="#11E5B4">The Journey</SectionLabel>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-[0.95]">
              Raced, Lived &<br />Volunteered
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              {MAP_LEGEND.map((l) => (
                <div key={l.type} className="flex items-center gap-2" data-testid={`legend-${l.type}`}>
                  <span className="w-3 h-3 rounded-full" style={{ background: l.color, boxShadow: `0 0 8px ${l.color}` }} />
                  <span className="font-display text-xs uppercase tracking-widest text-white/60">{l.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative border border-white/10 overflow-hidden" data-testid="world-map" style={{ height: "560px" }}>
            <MapContainer
              center={[38, -45]}
              zoom={2}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
              worldCopyJump
            >
              <TileLayer
                attribution='&copy; OpenStreetMap &copy; CARTO'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              <FlyController markerRefs={markerRefs} />
              <IntroFly />
              {MAP_LOCATIONS.map((loc) => (
                <Marker
                  key={loc.id}
                  position={[loc.lat, loc.lng]}
                  icon={makeIcon(loc.type)}
                  ref={(el) => { if (el) markerRefs.current[loc.id] = el; }}
                >
                  <Popup>
                    <div style={{ minWidth: 190 }}>
                      <p style={{ fontFamily: "Oswald, sans-serif", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 11, color: COLORS[loc.type], margin: 0 }}>{loc.type}</p>
                      <p style={{ fontFamily: "Oswald, sans-serif", fontWeight: 600, fontSize: 15, color: "#fff", margin: "4px 0 2px" }}>{loc.title}</p>
                      <p style={{ fontSize: 12, color: "#bbb", margin: "0 0 4px" }}>{loc.name}</p>
                      <p style={{ fontSize: 11, color: "#888", margin: 0 }}>{loc.note}</p>

                      {loc.photos && loc.photos.length > 0 ? (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, marginTop: 10 }}>
                          {loc.photos.slice(0, 6).map((src, idx) => {
                            const isLast = idx === 5 && loc.photos.length > 6;
                            return (
                              <div
                                key={idx}
                                onClick={() => window.dispatchEvent(new CustomEvent("reycro:lightbox", { detail: { photos: loc.photos, index: idx } }))}
                                style={{ position: "relative", cursor: "pointer", height: 52, overflow: "hidden" }}
                              >
                                <img src={src} alt={loc.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                {isLast && (
                                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Oswald, sans-serif", fontSize: 14, fontWeight: 600 }}>
                                    +{loc.photos.length - 6}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        loc.type !== "lived" && <p style={{ fontSize: 10, color: "#555", margin: "10px 0 0", textTransform: "uppercase", letterSpacing: "0.15em" }}>📷 Event photos coming soon</p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-5 text-sm text-white/40 text-center font-display uppercase tracking-widest">
            {MAP_LOCATIONS.length} pins · Raced, lived &amp; volunteered across 5 countries
          </p>
        </Reveal>
      </div>
    </section>
  );
};
